class RasaLogoDock extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.section = this.closest('.shopify-section');
    this.sentinel = this.previousElementSibling;
    this.target = this.querySelector('[data-rasa-logo-dock-target]');

    if (
      !this.section ||
      !this.target ||
      !this.sentinel?.matches('[data-rasa-logo-dock-sentinel]')
    ) {
      return;
    }

    if (this.sentinel.parentElement === this.section) {
      this.section.before(this.sentinel);
      this.sentinelWasMoved = true;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.handleScroll = () => this.scheduleFrame(false);
    this.handleResize = () => {
      this.bindScrollContainer();
      this.scheduleFrame(true);
    };
    this.handleVisibilityChange = () => {
      if (!document.hidden) this.scheduleFrame(true);
    };
    this.handleSectionLoad = (event) => {
      if (
        event.target === this.section ||
        event.target?.querySelector?.('[data-rasa-logo-dock-source]')
      ) {
        this.refreshElements();
      }
    };
    this.handleSectionUnload = (event) => {
      if (event.target === this.sourceSection) {
        this.hideMovingLayer();
        this.setTargetVisible(false);
        this.clearTargetSize();
        this.source = null;
        this.sourceVisual = null;
        this.sourceSection = null;
        this.metrics = null;
      }
    };

    window.addEventListener('resize', this.handleResize, { signal });
    window.addEventListener('orientationchange', this.handleResize, { signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
    document.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
    document.addEventListener('shopify:section:unload', this.handleSectionUnload, { signal });
    document.addEventListener('shopify:section:reorder', this.handleResize, { signal });

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleMotionChange = () => this.scheduleFrame(true);
    this.motionQuery.addEventListener('change', this.handleMotionChange);

    this.refreshElements();

    document.fonts?.ready.then(() => {
      if (this.isConnected) this.scheduleFrame(true);
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.motionQuery?.removeEventListener('change', this.handleMotionChange);
    this.scrollContainer?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();

    if (this.frameId) cancelAnimationFrame(this.frameId);
    this.movingLayer?.remove();

    if (this.sentinelWasMoved && this.sentinel?.isConnected) {
      this.sentinel.remove();
    }

    this.setSourceVisible(true);
    this.setTargetVisible(false);
    this.clearTargetSize();
    this.frameId = null;
    this.scrollContainer = null;
    this.sentinelWasMoved = false;
    this.isInitialized = false;
  }

  refreshElements() {
    this.source = this.findNearestSource();
    this.sourceVisual = this.source?.firstElementChild ?? null;
    this.sourceSection = this.source?.closest('.shopify-section') ?? null;

    if (!this.source || !this.sourceVisual) {
      this.hideMovingLayer();
      this.setTargetVisible(false);
      this.clearTargetSize();
      return;
    }

    this.buildMovingLayer();
    this.bindScrollContainer();
    this.observeSizes();

    const sourceImage = this.source.querySelector('img');
    if (sourceImage && !sourceImage.complete) {
      sourceImage.addEventListener('load', () => this.scheduleFrame(true), {
        once: true,
        signal: this.abortController.signal,
      });
    }

    this.scheduleFrame(true);
  }

  findNearestSource() {
    let section = this.section?.previousElementSibling;

    while (section) {
      const source = section.querySelector?.('[data-rasa-logo-dock-source]');
      if (source) return source;
      section = section.previousElementSibling;
    }

    return null;
  }

  buildMovingLayer() {
    this.movingLayer?.remove();

    const visual = this.sourceVisual?.cloneNode(true);
    if (!visual) return;

    visual.removeAttribute('id');
    visual.setAttribute('aria-hidden', 'true');
    visual.setAttribute('tabindex', '-1');
    visual.style.width = '100%';
    visual.style.maxWidth = '100%';
    visual.style.height = 'auto';
    visual.style.maxHeight = 'none';
    visual.style.objectFit = 'contain';

    if (visual instanceof HTMLImageElement) {
      visual.alt = '';
    }

    visual.querySelectorAll?.('[id]').forEach((element) => element.removeAttribute('id'));

    this.movingLayer = document.createElement('div');
    this.movingLayer.className = 'rasa-logo-dock-layer';
    this.movingLayer.setAttribute('aria-hidden', 'true');
    this.movingLayer.hidden = true;
    this.movingLayer.append(visual);
    document.body.append(this.movingLayer);
  }

  bindScrollContainer() {
    const nextContainer = this.findScrollContainer();
    if (nextContainer === this.scrollContainer) return;

    this.scrollContainer?.removeEventListener('scroll', this.handleScroll);
    this.scrollContainer = nextContainer;
    this.scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  findScrollContainer() {
    let element = this.section?.parentElement;

    while (element && element !== document.body) {
      const style = getComputedStyle(element);
      if (/(auto|scroll|overlay)/.test(style.overflowY)) return element;
      element = element.parentElement;
    }

    return window;
  }

  observeSizes() {
    this.resizeObserver?.disconnect();

    if (!('ResizeObserver' in window)) return;

    this.resizeObserver = new ResizeObserver(() => this.scheduleFrame(true));
    this.resizeObserver.observe(this.source);
    this.resizeObserver.observe(this.sourceVisual);
    this.resizeObserver.observe(this.target);
    this.resizeObserver.observe(this.section);
  }

  scheduleFrame(shouldMeasure) {
    this.needsMeasurement = this.needsMeasurement || shouldMeasure;
    if (this.frameId || document.hidden) return;

    this.frameId = requestAnimationFrame(() => {
      this.frameId = null;

      if (this.needsMeasurement) {
        this.needsMeasurement = false;
        if (!this.measure()) return;
      }

      this.render();
    });
  }

  measure() {
    if (
      !this.source?.isConnected ||
      !this.sourceVisual?.isConnected ||
      !this.target?.isConnected ||
      !this.sentinel?.isConnected ||
      !this.movingLayer
    ) {
      return false;
    }

    const scrollOffset = this.getScrollOffset();
    const rootTop = this.getScrollRootTop();
    const sourceRect = this.sourceVisual.getBoundingClientRect();

    if (sourceRect.width <= 0 || sourceRect.height <= 0) return false;

    this.setTargetSize(sourceRect.width, sourceRect.height);

    const targetRect = this.target.getBoundingClientRect();
    const sectionRect = this.section.getBoundingClientRect();
    const sentinelRect = this.sentinel.getBoundingClientRect();

    this.movingLayer.style.left = '0px';
    this.movingLayer.style.top = '0px';
    this.movingLayer.style.width = `${sourceRect.width}px`;
    this.movingLayer.style.height = 'auto';
    this.movingLayer.style.aspectRatio = `${sourceRect.width} / ${sourceRect.height}`;

    this.metrics = {
      endScroll: Math.max(1, scrollOffset + sentinelRect.top - rootTop),
      source: {
        x: sourceRect.left,
        y: sourceRect.top + scrollOffset,
        width: sourceRect.width,
        height: sourceRect.height,
      },
      target: {
        x: targetRect.left,
        y: rootTop + targetRect.top - sectionRect.top,
      },
    };

    return true;
  }

  render() {
    if (!this.metrics) return;

    const progress = this.clamp(this.getScrollOffset() / this.metrics.endScroll, 0, 1);
    this.applyProgress(progress);
  }

  applyProgress(progress) {
    if (this.motionQuery?.matches) {
      this.hideMovingLayer();
      this.setSourceVisible(progress < 1);
      this.setTargetVisible(progress >= 1);
      return;
    }

    if (progress <= 0.001) {
      this.hideMovingLayer();
      this.setSourceVisible(true);
      this.setTargetVisible(false);
      return;
    }

    if (progress >= 0.999) {
      this.hideMovingLayer();
      this.setSourceVisible(false);
      this.setTargetVisible(true);
      return;
    }

    this.setSourceVisible(false);
    this.setTargetVisible(false);

    const x = this.lerp(this.metrics.source.x, this.metrics.target.x, progress);
    const y = this.lerp(this.metrics.source.y, this.metrics.target.y, progress);

    this.movingLayer.hidden = false;
    this.movingLayer.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  setSourceVisible(isVisible) {
    if (!this.source) return;
    this.source.dataset.dockHidden = isVisible ? 'false' : 'true';
    this.source.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  setTargetVisible(isVisible) {
    if (!this.target) return;
    this.target.dataset.dockVisible = isVisible ? 'true' : 'false';
    this.target.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

    if (isVisible) {
      this.target.removeAttribute('tabindex');
    } else {
      this.target.setAttribute('tabindex', '-1');
    }
  }

  setTargetSize(width, height) {
    if (!this.target) return;

    const widthInPixels = `${width}px`;
    const aspectRatio = `${width} / ${height}`;

    this.target.style.width = widthInPixels;
    this.target.style.maxWidth = widthInPixels;
    this.target.style.height = 'auto';
    this.target.style.maxHeight = 'none';
    this.target.style.aspectRatio = aspectRatio;

    const targetVisual = this.target.firstElementChild;
    if (!targetVisual) return;

    targetVisual.style.width = '100%';
    targetVisual.style.maxWidth = '100%';
    targetVisual.style.height = '100%';
    targetVisual.style.maxHeight = 'none';
    targetVisual.style.objectFit = 'contain';
  }

  clearTargetSize() {
    if (!this.target) return;

    this.target.style.removeProperty('width');
    this.target.style.removeProperty('max-width');
    this.target.style.removeProperty('height');
    this.target.style.removeProperty('max-height');
    this.target.style.removeProperty('aspect-ratio');

    const targetVisual = this.target.firstElementChild;
    if (!targetVisual) return;

    targetVisual.style.removeProperty('width');
    targetVisual.style.removeProperty('max-width');
    targetVisual.style.removeProperty('height');
    targetVisual.style.removeProperty('max-height');
    targetVisual.style.removeProperty('object-fit');
  }

  hideMovingLayer() {
    if (this.movingLayer) this.movingLayer.hidden = true;
  }

  getScrollOffset() {
    return this.scrollContainer === window ? window.scrollY : this.scrollContainer?.scrollTop ?? 0;
  }

  getScrollRootTop() {
    return this.scrollContainer === window
      ? 0
      : this.scrollContainer?.getBoundingClientRect().top ?? 0;
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }
}

if (!customElements.get('rasa-logo-dock')) {
  customElements.define('rasa-logo-dock', RasaLogoDock);
}
